import { useQueryClient } from '@tanstack/react-query';
import useApi from './useApi';
import { useAuthInfo } from '@propelauth/react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface UseCheckoutOptions {
    onSuccess?: () => void;
}

interface OpenCheckoutOptions {
    quantity?: number;
}

const useLSCheckout = (options: UseCheckoutOptions = {}) => {
    const { i18n } = useTranslation();
    const { activeOrg } = useApi();
    const { orgId } = activeOrg;
    const { user } = useAuthInfo();

    const queryClient = useQueryClient();

    const handleLemonSqueezyEvent = event => {
        if (event.event === 'Checkout.Success') {
            queryClient.invalidateQueries({ queryKey: ['settings'] });
            options?.onSuccess?.();
        }
    };

    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://app.lemonsqueezy.com/js/lemon.js';
        script.async = true;

        script.onload = () => {
            if (window.createLemonSqueezy) {
                window.createLemonSqueezy();

                window.LemonSqueezy?.Setup({
                    eventHandler: handleLemonSqueezyEvent,
                });
            }
        };

        document.body.appendChild(script);

        return () => {
            document.body.removeChild(script);
        };
    }, []);

    const openCheckout = (options: OpenCheckoutOptions) => {
        const { quantity = 1 } = options;

        const baseUrl =
            'https://donbar.lemonsqueezy.com/checkout/buy/9a785739-8c2b-4b71-981d-36c3a0f29fe7?';
        const params = {
            embed: 1,
            quantity,
            'checkout[email]': user?.email,
            'checkout[billing_address][country]':
                i18n.language === 'he' ? 'IL' : 'US',
            'checkout[name]': user?.firstName + ' ' + user?.lastName,
            'checkout[custom][user_id]': user?.userId,
            'checkout[custom][org_id]': orgId,
        };
        const queryString = new URLSearchParams(params).toString();
        const checkoutUrl = `${baseUrl}${queryString}`;

        // 4. Open the overlay programmatically
        if (window.LemonSqueezy) {
            window.LemonSqueezy.Url.Open(checkoutUrl);
        } else {
            // Fallback: Redirect if script failed to load
            window.location.href = checkoutUrl;
        }
    };

    return openCheckout;
};

export default useLSCheckout;
